import React, { useMemo } from "react";
import Icon from "../Icon";

interface ButtonProps {
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  formType?: "button" | "submit" | "reset";
  disabled?: boolean;
  type?:
    | "primary"
    | "secondary"
    | "no-style"
    | "icon"
    | "icon no-style"
    | "outlined";
  icon?: string;
  iconPosition?: "start" | "end";
  size?: "sm" | "md" | "lg" | "xl";
}

const Button: React.FC<ButtonProps> = ({
  children,
  className,
  onClick,
  formType = "button",
  disabled,
  type = "primary",
  icon,
  iconPosition = "start",
  size = "md",
}) => {
  const typeClasses = useMemo(() => {
    if (type === "primary") {
      return "bg-orange active:border-orange text-white active:bg-gray-200 active:text-orange";
    } else if (type === "secondary") {
      return "bg-accent text-text hover:text-accent hover:bg-text active:bg-dark active:text-accent";
    } else if (type.includes("no-style")) {
      return "border-none";
    } else if (type === "icon") {
      return "bg-accent text-text hover:text-accent hover:bg-text active:bg-dark active:text-accent";
    } else if (type === "outlined") {
      return "active:border-orange text-orange hover:text-white hover:bg-orange active:bg-white active:text-white border border-orange";
    }
  }, [type]);

  const sizeClasses = useMemo(() => {
    if (type.includes("no-style")) {
      return "";
    }
    if (size === "sm") {
      return "py-3 px-3 text-sm";
    } else if (size === "md") {
      return "py-4 px-4 text-base";
    } else if (size === "lg") {
      return "py-5 px-6 text-lg";
    } else if (size === "xl") {
      return "py-6 px-8 text-xl";
    }
  }, [size, type]);

  const iconMarginClass: {
    start: string;
    end: string;
    size: string;
  } = useMemo(() => {
    if (type.includes("icon")) {
      return { start: "", end: "", size: "" };
    }
    if (size === "sm") {
      return { start: "mr-2", end: "ml-2", size: "" };
    } else if (size === "md") {
      return { start: "mr-2", end: "ml-2", size: "text-lg" };
    } else if (size === "lg") {
      return { start: "mr-4", end: "ml-4", size: "text-xl" };
    } else if (size === "xl") {
      return { start: "mr-2", end: "ml-2", size: "text-3xl" };
    } else {
      return { start: "", end: "", size: "" };
    }
  }, [size, type]);

  return (
    <button
      className={`flex items-center justify-start rounded-xl font-medium leading-none ${typeClasses} ${sizeClasses} border border-light-grey focus:outline-none transition ease-in-out duration-100 disabled:cursor-not-allowed disabled:bg-light-grey disabled:text-secondary ${
        className ? className : ""
      }`}
      onClick={onClick}
      type={formType}
      disabled={disabled}
    >
      {icon && iconPosition === "start" && (
        <Icon
          className={[iconMarginClass.start, iconMarginClass.size].join(" ")}
        >
          {icon}
        </Icon>
      )}
      {!type.includes("icon") && (
        <span className="flex-1 whitespace-no-wrap">{children}</span>
      )}
      {icon && iconPosition === "end" && (
        <Icon className={[iconMarginClass.end, iconMarginClass.size].join(" ")}>
          {icon}
        </Icon>
      )}
    </button>
  );
};

export default Button;
